import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import axios from 'axios';
import { ethers } from 'ethers';

import E3DTokenABI from './E3DToken_ABI.json';
import E3DNFTManagerABI from './E3DNFTManager_ABI.json';

// Same reasoning as PayPage.js: this page only ever needs to call back to the
// host that served it, so it deliberately does not use the app's configurable
// SERVER_NAME constant.
const API_BASE = '/api';

const E3D_TOKEN_ADDRESS = '0x6488861b401F427D13B6619C77C297366bCf6386';
const NFT_MANAGER_ADDRESS = '0xeED4620ff525101Ffcf7327378232CA9EF778D47';
const MINT_FEE = ethers.utils.parseUnits('100', 18);
const ETHEREUM_CHAIN_ID = 1;
const ETHEREUM_CHAIN_ID_HEX = `0x${ETHEREUM_CHAIN_ID.toString(16)}`;

// E3DNFTManager lives on Ethereum mainnet only -- unlike PayPage.js (which
// supports both Base and Ethereum depending on payment method), minting always
// targets this one chain, so there's only one add-chain fallback to define.
const ETHEREUM_ADD_PARAMS = {
  chainName: 'Ethereum Mainnet',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: ['https://ethereum.publicnode.com'],
  blockExplorerUrls: ['https://etherscan.io'],
};

const useStyles = makeStyles(() => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    backgroundColor: 'rgba(5,8,20,0.98)',
    minHeight: '100vh',
    padding: 24,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.22), rgba(10,14,30,0.98))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    padding: 24,
    boxSizing: 'border-box',
  },
  title: {
    fontSize: 20,
    fontWeight: 900,
    marginBottom: 12,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 8,
    fontSize: 14,
    color: 'rgba(255,255,255,0.85)',
  },
  status: {
    marginTop: 16,
    fontSize: 14,
    color: 'rgba(255,255,255,0.7)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  error: {
    marginTop: 16,
    fontSize: 14,
    color: '#ff6b6b',
  },
  debug: {
    marginTop: 16,
    padding: 12,
    fontSize: 11,
    lineHeight: 1.5,
    color: 'rgba(255,255,255,0.6)',
    background: 'rgba(0,0,0,0.4)',
    borderRadius: 8,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    userSelect: 'text',
  },
  button: {
    marginTop: 20,
    width: '100%',
  },
  link: {
    color: '#8ab4ff',
  },
}));

function useQueryParam(name) {
  const location = useLocation();
  return new URLSearchParams(location.search).get(name);
}

export default function MintReportPage() {
  const classes = useStyles();
  const sessionId = useQueryParam('session');

  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('loading');
  const [debugInfo, setDebugInfo] = useState(null);
  const [txHash, setTxHash] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError('Missing "session" parameter in the URL.');
      setStatus('error');
      return undefined;
    }

    let cancelled = false;
    const requestUrl = `${API_BASE}/mint/session/${encodeURIComponent(sessionId)}`;
    axios.get(requestUrl)
      .then((res) => {
        if (cancelled) return;
        setSession(res.data);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || 'This mint link is invalid or has expired.');
        setDebugInfo({
          requestUrl,
          httpStatus: err?.response?.status ?? null,
          responseBody: err?.response?.data ?? null,
          errorMessage: err?.message ?? null,
          errorCode: err?.code ?? null,
          isNetworkError: !err?.response,
        });
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const handleMint = useCallback(async () => {
    if (!session) return;
    if (!window.ethereum) {
      setError('No wallet extension detected. Install MetaMask (or a compatible wallet) and reload this page.');
      setStatus('error');
      return;
    }

    try {
      setError(null);
      setStatus('connecting');

      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const [account] = await provider.send('eth_requestAccounts', []);

      if (String(account).toLowerCase() !== String(session.wallet).toLowerCase()) {
        setError(`This mint is for wallet ${session.wallet}, but your connected wallet is ${account}. Switch accounts in your wallet extension and reload this page.`);
        setStatus('error');
        return;
      }

      const network = await provider.getNetwork();
      if (Number(network.chainId) !== ETHEREUM_CHAIN_ID) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: ETHEREUM_CHAIN_ID_HEX }],
          });
        } catch (switchError) {
          if (switchError && switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{ chainId: ETHEREUM_CHAIN_ID_HEX, ...ETHEREUM_ADD_PARAMS }],
            });
          } else {
            throw switchError;
          }
        }
      }

      const signer = provider.getSigner();
      const nftContract = new ethers.Contract(NFT_MANAGER_ADDRESS, E3DNFTManagerABI, signer);

      setStatus('checking-subscription');
      let hasFreeMints = false;
      try {
        const [, , remainingMints, isActive] = await nftContract.getSubscriptionDetails(account);
        hasFreeMints = Boolean(isActive) && Number(remainingMints) > 0;
      } catch (subscriptionError) {
        // No subscription, or the read failed -- fall back to the paid mint path.
        hasFreeMints = false;
      }

      const ensureE3dApproval = async () => {
        const e3dToken = new ethers.Contract(E3D_TOKEN_ADDRESS, E3DTokenABI, signer);
        const currentAllowance = await e3dToken.allowance(account, NFT_MANAGER_ADDRESS);
        if (currentAllowance && currentAllowance.gte && currentAllowance.gte(MINT_FEE)) return;
        setStatus('approving');
        const approvalTx = await e3dToken.approve(NFT_MANAGER_ADDRESS, MINT_FEE);
        await approvalTx.wait();
      };

      if (!hasFreeMints) {
        await ensureE3dApproval();
      }

      setStatus('minting');
      let mintTx;
      try {
        mintTx = await nftContract.mintNFT(session.metadataURI, []);
      } catch (mintError) {
        const combined = `${mintError?.reason || ''} ${mintError?.message || ''}`.toLowerCase();
        if (combined.includes('approve e3d tokens first')) {
          await ensureE3dApproval();
          setStatus('minting');
          mintTx = await nftContract.mintNFT(session.metadataURI, []);
        } else {
          throw mintError;
        }
      }

      setStatus('waiting-confirmation');
      await mintTx.wait();
      setTxHash(mintTx.hash);

      setStatus('reporting');
      await axios.post(`${API_BASE}/mint/session/${encodeURIComponent(sessionId)}/complete`, {
        txHash: mintTx.hash,
      });

      setStatus('done');
    } catch (err) {
      setError(err?.response?.data?.error || err?.reason || err?.message || 'Mint failed. Please try again.');
      setDebugInfo({
        httpStatus: err?.response?.status ?? null,
        responseBody: err?.response?.data ?? null,
        errorMessage: err?.message ?? null,
        errorCode: err?.code ?? null,
        isNetworkError: err && !err.response && !err.reason,
      });
      setStatus('error');
    }
  }, [session, sessionId]);

  const statusMessages = {
    connecting: 'Connecting to your wallet...',
    'checking-subscription': 'Checking for a free mint...',
    approving: 'Approving E3D token spending...',
    minting: 'Waiting for you to approve the mint in your wallet...',
    'waiting-confirmation': 'Transaction submitted — waiting for confirmation on-chain...',
    reporting: 'Mint confirmed — finishing up...',
  };

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <Typography className={classes.title}>e3d — mint report NFT</Typography>

        {status === 'loading' && (
          <div className={classes.status}>
            <CircularProgress size={16} style={{ color: '#ffffff' }} />
            <span>Loading mint details...</span>
          </div>
        )}

        {session && (status !== 'loading' && status !== 'done') && (
          <>
            <div className={classes.row}><span>Wallet</span><span>{session.wallet}</span></div>
            <div className={classes.row}><span>Network</span><span>Ethereum Mainnet</span></div>
            <div className={classes.row}><span>Cost</span><span>100 E3D + gas (free with an active subscription)</span></div>
          </>
        )}

        {status === 'ready' && (
          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            onClick={handleMint}
          >
            Connect wallet & mint
          </Button>
        )}

        {statusMessages[status] && (
          <div className={classes.status}>
            <CircularProgress size={16} style={{ color: '#ffffff' }} />
            <span>{statusMessages[status]}</span>
          </div>
        )}

        {status === 'done' && (
          <>
            <Typography className={classes.status}>
              Mint confirmed — you can close this tab and return to your terminal.
            </Typography>
            {txHash && (
              <Typography className={classes.status}>
                <a className={classes.link} href={`https://etherscan.io/tx/${txHash}`} target="_blank" rel="noreferrer">
                  View on Etherscan
                </a>
              </Typography>
            )}
          </>
        )}

        {error && <Typography className={classes.error}>{error}</Typography>}

        {debugInfo && (
          <pre className={classes.debug}>{JSON.stringify(debugInfo, null, 2)}</pre>
        )}
      </div>
    </div>
  );
}
